/* useAntiCheat — detects tab/window blur during a question.
 *
 *  Behaviour:
 *  - First blur during a round → show a warning toast/banner
 *  - Second blur (or stays-blurred for >3s on the first) → call `onForfeit`
 *    so the caller can submit a forced wrong answer for the round.
 *  - Counters reset whenever `roundKey` changes.
 *
 *  Also reports a `flagSuspiciousIfFast(answer, elapsedMs)` helper so the
 *  caller can attach a `suspicious: true` flag to the answer payload when
 *  the user submitted a non-trivial answer faster than `minMs`.
 */
import { useEffect, useRef, useState } from "react";

export default function useAntiCheat({
  enabled = true,
  roundKey,
  onForfeit,
  warnText = "Tab switching is not allowed during a question. Next time will forfeit the round.",
  fastSubmitMs = 1500,
}) {
  const [blurCount, setBlurCount] = useState(0);
  const [warning, setWarning] = useState(null);
  const blurCountRef = useRef(0);
  const blurStartRef = useRef(0);
  const forfeitedRef = useRef(false);
  const onForfeitRef = useRef(onForfeit);
  onForfeitRef.current = onForfeit;

  // Reset for each new round
  useEffect(() => {
    setBlurCount(0);
    blurCountRef.current = 0;
    forfeitedRef.current = false;
    setWarning(null);
  }, [roundKey]);

  useEffect(() => {
    if (!enabled) return;
    let stayBlurredTimer = null;
    let lastFireTs = 0;

    const onHide = () => {
      if (forfeitedRef.current) return;
      // De-dup: visibilitychange + window.blur both fire on the same tab
      // switch on most browsers. Collapse anything within 400ms into one event.
      const now = Date.now();
      if (now - lastFireTs < 400) return;
      lastFireTs = now;
      blurStartRef.current = now;
      blurCountRef.current += 1;
      setBlurCount(blurCountRef.current);

      if (blurCountRef.current === 1) {
        setWarning(warnText);
        // If they stay hidden for >3s on the first warning, count as forfeit
        stayBlurredTimer = setTimeout(() => {
          if (document.hidden && !forfeitedRef.current) {
            forfeitedRef.current = true;
            setWarning("Round forfeited — tab/window left for too long.");
            try { onForfeitRef.current && onForfeitRef.current("stay_hidden"); } catch {}
          }
        }, 3000);
      } else if (blurCountRef.current >= 2 && !forfeitedRef.current) {
        forfeitedRef.current = true;
        setWarning("Round forfeited — second tab switch detected.");
        try { onForfeitRef.current && onForfeitRef.current("repeat_blur"); } catch {}
      }
    };

    const onShow = () => {
      if (stayBlurredTimer) { clearTimeout(stayBlurredTimer); stayBlurredTimer = null; }
    };

    const onVis = () => {
      if (document.hidden) onHide();
      else onShow();
    };
    // Only count window.blur if the document is actually hidden — otherwise
    // it's just an iframe / devtools focus shift, not a real tab switch.
    const onWinBlur = () => { if (document.hidden) onHide(); };
    const onWinFocus = () => onShow();

    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("blur", onWinBlur);
    window.addEventListener("focus", onWinFocus);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("blur", onWinBlur);
      window.removeEventListener("focus", onWinFocus);
      if (stayBlurredTimer) clearTimeout(stayBlurredTimer);
    };
  }, [enabled, warnText]);

  const flagSuspiciousIfFast = (answer, elapsedMs) => {
    const a = String(answer || "").trim();
    if (!a) return false;
    if (elapsedMs >= fastSubmitMs) return false;
    // Trivial single-character or numeric answers can legitimately be fast.
    if (a.length <= 2) return false;
    if (/^-?\d+(\.\d+)?$/.test(a)) return false;
    return true;
  };

  return {
    blurCount,
    warning,
    dismissWarning: () => setWarning(null),
    forfeited: forfeitedRef.current,
    flagSuspiciousIfFast,
  };
}
